import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trophy } from 'lucide-react';
import { GameData } from '../game/types';
import { BrancheFlorale, FleurCoin } from '../components/Florals';

interface Props {
  data: GameData;
  onCommencer: (prenom: string) => void;
}

export default function Accueil({ data, onCommencer }: Props) {
  const [prenom, setPrenom] = useState('');
  const valide = prenom.trim().length >= 2;

  return (
    <div className="min-h-screen bg-white flex flex-col relative overflow-hidden">
      <FleurCoin className="absolute top-3 right-3 w-14 h-14 opacity-80" />
      <main className="flex-1 flex flex-col px-6 pt-14 pb-8 max-w-md w-full mx-auto">
        <div className="anim-apparition">
          <p className="petit-cap">{data.config.lieu} · {data.config.dateMariage}</p>
          <h1 className="font-script text-encre text-6xl leading-tight mt-4">{data.config.titre}</h1>
          <p className="text-encre font-semibold tracking-[0.3em] uppercase text-base mt-1">
            {data.config.sousTitre}
          </p>
        </div>

        <div className="mt-10 space-y-4 anim-apparition" style={{ animationDelay: '0.15s' }}>
          {[
            ['I', 'La galerie', 'Cinq questions dont les réponses se cachent parmi les portraits du château.'],
            ['II', 'Les tatoués', "Dix invités portent le tatouage des mariés. Retrouvez-les : chacun détient un indice, à l'oral."],
            ['III', 'Les serrures', 'Trois mots-clés à percer. Le chrono s\'arrête à la troisième serrure.'],
          ].map(([num, titre, texte]) => (
            <div key={num} className="flex gap-4 items-start">
              <span className="shrink-0 w-9 h-9 rounded-full border border-encre text-encre flex items-center justify-center font-semibold text-sm">
                {num}
              </span>
              <div>
                <p className="font-semibold text-encre">{titre}</p>
                <p className="text-sm text-texte leading-relaxed">{texte}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 anim-apparition" style={{ animationDelay: '0.3s' }}>
          <label htmlFor="prenom" className="petit-cap block mb-2">
            Votre prénom, noble convive
          </label>
          <input
            id="prenom"
            data-testid="pseudo-input"
            type="text"
            value={prenom}
            maxLength={20}
            onChange={(e) => setPrenom(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && valide && onCommencer(prenom)}
            placeholder="Camille"
            autoComplete="given-name"
            className="w-full min-h-[52px] px-5 rounded-lg border border-encre bg-white text-lg text-texte placeholder:text-encre-clair/60"
          />
          <p className="text-sm text-encre-clair mt-3">
            Le chrono s'élance dès que vous validez. Bonne course.
          </p>
          <button
            data-testid="commencer-btn"
            className="btn-principal mt-4"
            disabled={!valide}
            onClick={() => onCommencer(prenom)}
          >
            Que la quête commence
          </button>
          <Link
            to="/classement"
            data-testid="lien-classement-accueil"
            className="mt-4 min-h-[44px] flex items-center justify-center gap-2 text-encre-clair text-sm font-medium"
          >
            <Trophy size={16} /> Voir le classement en direct
          </Link>
        </div>

        <div className="mt-auto pt-10 flex justify-center">
          <BrancheFlorale className="w-52" />
        </div>
      </main>
    </div>
  );
}
